<script setup lang="ts">
import { computed, type PropType } from 'vue';
import GaiaButton from './GaiaButton.vue';
import { gaiaGlyphPaths, gaiaGlyphSize } from '../glyphs';

const props = defineProps({
  /**
   * What is being brought into existence — a noun ("calendar", "playbook", "view").
   * Composes into "Create view", which becomes the visible label, so the verb
   * can't drift into "New view" at one call site and "Create view" at the next.
   * A caller whose label genuinely isn't verb + noun passes the default slot.
   */
  subject: {
    type: String,
    default: '',
  },
  /**
   * `primary` is the rule: a create is the focal action of the view, the empty
   * state or the dialog it sits in. `default` is for a create offered beside
   * other choices — the signing picker's two "Create …" escape hatches.
   */
  variant: {
    type: String as PropType<'primary' | 'default'>,
    default: 'primary',
  },
  /** `md` for a page header or an empty state, `sm` for a dialog's commit. */
  size: {
    type: String as PropType<'sm' | 'md'>,
    default: 'md',
  },
  /**
   * The plus is an affordance — it says "this makes something". It earns its
   * place in a page header, where the button competes with the rest of the
   * toolbar. In an empty state the dashed icon box above already carries the
   * picture, and on a dialog's commit the button is the only thing to press;
   * both read cleaner without it, so those call sites turn it off.
   */
  icon: {
    type: Boolean,
    default: true,
  },
  /** Spells out why the action is unavailable; the label needs no tooltip. */
  title: {
    type: String,
    default: '',
  },
});

const text = computed(() => (props.subject ? `Create ${props.subject}` : 'Create'));

const glyphSize = computed(() => gaiaGlyphSize[props.size]);
</script>

<template>
  <!-- The plus for making a new record — a calendar, a playbook, a signature
       request, a view.

       Create vs Add is a judgement call for the screen rather than something to
       derive: an `GaiaAddButton` often writes a row too, and "Add action" reads
       better than "Create action" where the action belongs to an option. Pick
       what fits the surface. Both components exist so the glyph, the size scale
       and the verb + noun label composition sit in one place instead of being
       re-implemented at every call site. -->
  <gaia-button
    :type="props.variant === 'primary' ? 'primary' : undefined"
    :size="props.size === 'md' ? 'medium' : 'small'"
    :title="props.title || undefined"
  >
    <template v-if="props.icon" #icon>
      <svg
        :width="glyphSize"
        :height="glyphSize"
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path :d="gaiaGlyphPaths.plus" />
      </svg>
    </template>
    <slot>{{ text }}</slot>
  </gaia-button>
</template>
